/**
 * CRM Pipeline Query Hooks
 *
 * TanStack Query hooks for pipeline data fetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getPipelineBoard,
  getPipelineSummary,
  createOpportunity,
  moveOpportunityStage,
  transformOpportunityToQuote,
  markOpportunityLost,
  loadMoreForStage,
  getAccountManagers,
} from '../api/crmApi';
import type {
  PipelineFilters,
  CreateOpportunityInput,
  MoveOpportunityInput,
  TransformToQuoteInput,
  PipelineStage,
  PipelineBoard,
  PipelineOpportunity,
} from '../types';
import { CACHE_STALE_TIMES } from '../constants';
import { getErrorMessage } from '../utils/getErrorMessage';

// =============================================================================
// Query Key Factory
// =============================================================================

export const crmPipelineKeys = {
  all: ['crm-pipeline'] as const,
  board: () => [...crmPipelineKeys.all, 'board'] as const,
  boardFiltered: (filters?: PipelineFilters) => [...crmPipelineKeys.board(), filters] as const,
  summary: () => [...crmPipelineKeys.all, 'summary'] as const,
  summaryFiltered: (filters?: PipelineFilters) => [...crmPipelineKeys.summary(), filters] as const,
  accountManagers: () => [...crmPipelineKeys.all, 'account-managers'] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch the full pipeline board
 */
export function useCrmPipelineBoard(filters?: PipelineFilters) {
  return useQuery({
    queryKey: crmPipelineKeys.boardFiltered(filters),
    queryFn: () => getPipelineBoard(filters),
    staleTime: CACHE_STALE_TIMES.PIPELINE_BOARD,
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch pipeline summary metrics only
 */
export function useCrmPipelineSummary(filters?: PipelineFilters) {
  return useQuery({
    queryKey: crmPipelineKeys.summaryFiltered(filters),
    queryFn: () => getPipelineSummary(filters),
    staleTime: CACHE_STALE_TIMES.PIPELINE_SUMMARY,
  });
}

/**
 * Hook to fetch distinct account managers for filtering
 */
export function useAccountManagers() {
  return useQuery({
    queryKey: crmPipelineKeys.accountManagers(),
    queryFn: () => getAccountManagers(),
    staleTime: CACHE_STALE_TIMES.ACCOUNT_MANAGERS,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Hook to create a new opportunity
 */
export function useCreateOpportunity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateOpportunityInput) => createOpportunity(input),

    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: crmPipelineKeys.board() });
      queryClient.invalidateQueries({ queryKey: crmPipelineKeys.summary() });
      toast.success('Opportunity created', { description: result.title });
    },
    onError: (error) => {
      toast.error('Failed to create opportunity', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to move an opportunity to a new stage
 */
export function useMoveOpportunityStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      opportunityId,
      input,
    }: {
      accountId: string | null | undefined;
      opportunityId: string;
      input: MoveOpportunityInput;
    }) => moveOpportunityStage(accountId, opportunityId, input),

    // Optimistic update
    onMutate: async ({ opportunityId, input }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: crmPipelineKeys.board() });

      // Snapshot the previous value
      const previousBoard = queryClient.getQueriesData({ queryKey: crmPipelineKeys.board() });

      // Optimistically update the board
      queryClient.setQueriesData({ queryKey: crmPipelineKeys.board() }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const board = old as {
          columns: Array<{
            key: PipelineStage;
            count: number;
            opportunities: Array<{ id: string; stage: PipelineStage }>;
          }>;
        };

        // Find and move the opportunity
        let movedOpp: { id: string; stage: PipelineStage } | undefined;
        const newColumns = board.columns.map((col) => {
          const oppIndex = col.opportunities.findIndex((o) => o.id === opportunityId);
          if (oppIndex !== -1) {
            movedOpp = { ...col.opportunities[oppIndex], stage: input.target_stage };
            return {
              ...col,
              opportunities: col.opportunities.filter((_, i) => i !== oppIndex),
              count: col.count - 1,
            };
          }
          return col;
        });

        // Add to target column
        if (movedOpp) {
          const targetColIndex = newColumns.findIndex((c) => c.key === input.target_stage);
          if (targetColIndex !== -1) {
            newColumns[targetColIndex] = {
              ...newColumns[targetColIndex],
              opportunities: [...newColumns[targetColIndex].opportunities, movedOpp],
              count: newColumns[targetColIndex].count + 1,
            };
          }
        }

        return { ...board, columns: newColumns };
      });

      return { previousBoard };
    },

    // Rollback on error
    onError: (error, _variables, context) => {
      if (context?.previousBoard) {
        context.previousBoard.forEach(([key, data]) => {
          queryClient.setQueryData(key, data);
        });
      }
      toast.error('Failed to move opportunity', { description: getErrorMessage(error) });
    },

    // Refetch after settle
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: crmPipelineKeys.board() });
      queryClient.invalidateQueries({ queryKey: crmPipelineKeys.summary() });
    },
  });
}

/**
 * Hook to transform an opportunity to a quote
 */
export function useTransformToQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      opportunityId,
      input,
    }: {
      accountId: string;
      opportunityId: string;
      input: TransformToQuoteInput;
    }) => transformOpportunityToQuote(accountId, opportunityId, input),

    onSuccess: (result) => {
      // Invalidate pipeline board (opportunity will be removed)
      queryClient.invalidateQueries({ queryKey: crmPipelineKeys.board() });
      queryClient.invalidateQueries({ queryKey: crmPipelineKeys.summary() });
      // Also invalidate account quotes and dashboard since a new quote was created
      queryClient.invalidateQueries({ queryKey: ['account-quotes'] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard'] });
      toast.success('Quote created successfully', {
        description: `Quote ${result.quote_number} has been created`,
      });
    },
    onError: (error) => {
      toast.error('Failed to create quote', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to mark an opportunity as lost
 */
export function useMarkOpportunityLost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      opportunityId,
      reason,
    }: {
      accountId: string | null | undefined;
      opportunityId: string;
      reason?: string;
    }) => markOpportunityLost(accountId, opportunityId, reason),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmPipelineKeys.board() });
      queryClient.invalidateQueries({ queryKey: crmPipelineKeys.summary() });
      toast.success('Opportunity marked as lost');
    },
    onError: (error) => {
      toast.error('Failed to mark opportunity as lost', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to load more opportunities for a specific stage
 */
export function useLoadMoreForStage(filters?: PipelineFilters) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      stage,
      offset,
    }: {
      stage: PipelineStage;
      offset: number;
    }) => loadMoreForStage(stage, offset, filters),

    onSuccess: (result) => {
      // Update the board data by appending new opportunities to the stage
      queryClient.setQueriesData({ queryKey: crmPipelineKeys.board() }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const board = old as PipelineBoard;

        const newColumns = board.columns.map((col) => {
          if (col.key !== result.stage) return col;

          // Append new opportunities and update metadata
          const existingIds = new Set(col.opportunities.map((o: PipelineOpportunity) => o.id));
          const newOpps = result.opportunities.filter((o) => !existingIds.has(o.id));

          return {
            ...col,
            opportunities: [...col.opportunities, ...newOpps],
            loadedCount: col.loadedCount + newOpps.length,
            hasMore: result.has_more,
          };
        });

        return { ...board, columns: newColumns };
      });
    },
    onError: (error) => {
      toast.error('Failed to load more opportunities', { description: getErrorMessage(error) });
    },
  });
}
